/**
 * Requirement Extraction Page
 * Extracts requirements from uploaded RFP documents
 */

import React, { useState } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import { Card, Button, Input, Badge, Table, LoadingSpinner, Modal } from '@tracekit/core';
import { SourceList, uploadArtifact, listArtifacts } from '@tracekit/core';
import { useWorkspace } from '@tracekit/core';
import { ListChecks, Upload, FileText, CheckCircle, XCircle, Edit2 } from 'lucide-react';
import { extractRequirements } from '../services/rfpApi';
import { Requirement } from '../types/rfp';
import { MoEOrchestrator } from '../components/MoEOrchestrator';
import { Breadcrumbs } from '../components/Breadcrumbs';
import { useToastStore } from '@tracekit/core';

export function RequirementExtractionPage() {
  const { addToast } = useToastStore();
  const { projectId } = useWorkspace();
  const [selectedRfpId, setSelectedRfpId] = useState<string | null>(null);
  const [requirements, setRequirements] = useState<Requirement[]>([]);
  const [isExtracting, setIsExtracting] = useState(false);
  const [editingRequirement, setEditingRequirement] = useState<Requirement | null>(null);

  // Fetch artifacts to allow selection
  const { data: artifacts } = useQuery({
    queryKey: ['artifacts', projectId, 'rfp_document'],
    queryFn: () => listArtifacts(projectId!),
    enabled: !!projectId,
  });

  // Filter to RFP documents
  const rfpArtifacts = React.useMemo(() => {
    if (!artifacts) return [];
    return artifacts.filter((a: any) => 
      a.artifact_type === 'rfp_document' || 
      a.filename?.toLowerCase().includes('rfp') ||
      a.filename?.toLowerCase().includes('request for proposal')
    );
  }, [artifacts]);

  const handleExtract = async () => {
    if (!projectId || !selectedRfpId) return;

    setIsExtracting(true);
    try {
      const extracted = await extractRequirements(projectId, selectedRfpId);
      setRequirements(extracted);
    } catch (error: any) {
      console.error('Extraction failed:', error);
      addToast({
        type: 'error',
        title: 'Extraction failed',
        message: error.message || 'Failed to extract requirements',
      });
    } finally {
      setIsExtracting(false);
    }
  };

  const handleRequirementChange = (req: Requirement, field: keyof Requirement, value: any) => {
    setRequirements((prev) =>
      prev.map((r) => (r.id === req.id ? { ...r, [field]: value, updated_at: new Date().toISOString() } : r))
    );
  };

  const handleExport = (format: 'csv' | 'json') => {
    if (format === 'csv') {
      const headers = ['ID', 'Text', 'Section', 'Page', 'Confidence', 'Status', 'Response'];
      const rows = requirements.map((r) => [
        r.id,
        r.text,
        r.section,
        r.page || '',
        r.confidence.toFixed(2),
        r.status,
        r.keystone_response,
      ]);
      const csv = [headers, ...rows].map((row) => row.map((cell) => `"${cell}"`).join(',')).join('\n');
      const blob = new Blob([csv], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `rfp-requirements-${new Date().toISOString().split('T')[0]}.csv`;
      a.click();
    } else {
      const blob = new Blob([JSON.stringify(requirements, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `rfp-requirements-${new Date().toISOString().split('T')[0]}.json`;
      a.click();
    }
  };

  return (
    <div className="container mx-auto p-6">
      <Breadcrumbs items={[
        { label: 'RFP Proposals', path: '/rfp/proposals' },
        { label: 'Extract Requirements' },
      ]} />
      <div className="mb-6">
        <h1 className="text-2xl font-bold">Extract RFP Requirements</h1>
        <p className="text-gray-600 mt-1">Upload RFP and extract all mandatory requirements with citations</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-6 mb-6">
        <Card title="Upload RFP Document">
          <div className="space-y-4">
            <p className="text-sm text-gray-600">
              Upload an RFP document to extract requirements. The document will be indexed and requirements will be extracted with page citations.
            </p>
            <SourceList />
            
            {/* RFP Artifact Selector */}
            {rfpArtifacts.length > 0 && (
              <div className="mt-4">
                <label className="block text-sm font-medium mb-2">Or select existing RFP:</label>
                <select
                  value={selectedRfpId || ''}
                  onChange={(e) => setSelectedRfpId(e.target.value || null)}
                  className="w-full px-3 py-2 border rounded-lg"
                  aria-label="Select RFP document"
                >
                  <option value="">-- Select RFP Document --</option>
                  {rfpArtifacts.map((artifact: any) => (
                    <option key={artifact.id} value={artifact.id}>
                      {artifact.filename} ({new Date(artifact.created_at).toLocaleDateString()})
                    </option>
                  ))}
                </select>
              </div>
            )}

            {selectedRfpId && (
              <div className="mt-4 p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg text-gray-900 dark:text-gray-100">
                <p className="text-sm font-medium">RFP Selected</p>
                <p className="text-xs text-gray-600 dark:text-gray-400">Artifact ID: {selectedRfpId}</p>
                <p className="text-xs text-gray-600">
                  {rfpArtifacts.find((a: any) => a.id === selectedRfpId)?.filename}
                </p>
              </div>
            )}
          </div>
        </Card>

        <Card title="Extraction Status">
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600">Requirements Extracted</span>
              <Badge>{requirements.length}</Badge>
            </div>
            {requirements.length > 0 && (
              <div className="space-y-2">
                <div className="flex items-center justify-between text-sm">
                  <span>Average Confidence</span>
                  <span className="font-medium">
                    {(requirements.reduce((sum, r) => sum + r.confidence, 0) / requirements.length * 100).toFixed(1)}%
                  </span>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span>Complete</span>
                  <span className="font-medium">
                    {requirements.filter((r) => r.status === 'Complete').length} / {requirements.length}
                  </span>
                </div>
              </div>
            )}
            <Button
              onClick={handleExtract}
              disabled={!selectedRfpId || isExtracting}
              className="w-full"
            >
              {isExtracting ? (
                <>
                  <LoadingSpinner className="mr-2" />
                  Extracting...
                </>
              ) : (
                <>
                  <ListChecks className="h-4 w-4 mr-2" />
                  Extract Requirements
                </>
              )}
            </Button>
          </div>
        </Card>
      </div>

      {requirements.length > 0 && (
        <Card title="Extracted Requirements">
          <div className="mb-4 flex items-center justify-between">
            <div className="flex gap-2">
              <Button variant="primary" size="sm" onClick={() => handleExport('csv')}>
                Export CSV
              </Button>
              <Button variant="primary" size="sm" onClick={() => handleExport('json')}>
                Export JSON
              </Button>
            </div>
          </div>

          <div className="overflow-x-auto">
            <Table
              data={requirements}
              columns={[
              {
                header: '#',
                key: 'id',
                render: (value: any, row: Requirement) => (
                  <div className="font-mono text-sm">{row.id}</div>
                ),
              },
              {
                header: 'Requirement Text',
                key: 'text',
                render: (value: any, row: Requirement) => (
                  <div className="max-w-md">
                    <p className="text-sm">{row.text}</p>
                    <div className="flex items-center gap-2 mt-1">
                      <Badge variant="default" className="text-xs">
                        {row.section}
                      </Badge>
                      {row.page && (
                        <span className="text-xs text-gray-500">Page {row.page}</span>
                      )}
                      <Badge
                        variant={row.confidence >= 0.75 ? 'default' : row.confidence >= 0.6 ? 'default' : 'default'}
                        className="text-xs"
                      >
                        {Math.round(row.confidence * 100)}%
                      </Badge>
                    </div>
                  </div>
                ),
              },
              {
                header: 'Keystone Response',
                key: 'keystone_response',
                render: (value: any, row: Requirement) => (
                  <div className="max-w-md">
                    {row.keystone_response ? (
                      <p className="text-sm">{row.keystone_response}</p>
                    ) : (
                      <p className="text-sm text-gray-400 italic">Not yet addressed</p>
                    )}
                  </div>
                ),
              },
              {
                header: 'Status',
                key: 'status',
                render: (value: any, row: Requirement) => (
                  <select
                    value={row.status}
                    onChange={(e) => handleRequirementChange(row, 'status', e.target.value)}
                    className="px-2 py-1 border rounded text-sm"
                  >
                    <option value="Pending">Pending</option>
                    <option value="In Progress">In Progress</option>
                    <option value="Complete">Complete</option>
                  </select>
                ),
              },
              {
                header: 'Actions',
                key: 'id',
                render: (value: any, row: Requirement) => (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setEditingRequirement(row)}
                  >
                    <Edit2 className="h-4 w-4" />
                  </Button>
                ),
              },
            ]}
            />
          </div>
        </Card>
      )}

      {editingRequirement && (
        <Modal
          isOpen={!!editingRequirement}
          onClose={() => setEditingRequirement(null)}
          title="Edit Requirement"
        >
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-1">Requirement Text</label>
              <textarea
                value={editingRequirement.text}
                onChange={(e) => setEditingRequirement({ ...editingRequirement, text: e.target.value })}
                className="w-full px-3 py-2 border rounded-lg"
                rows={3}
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Keystone Response</label>
              <textarea
                value={editingRequirement.keystone_response}
                onChange={(e) => setEditingRequirement({ ...editingRequirement, keystone_response: e.target.value })}
                className="w-full px-3 py-2 border rounded-lg"
                rows={4}
                placeholder="Enter how Keystone will address this requirement..."
              />
            </div>
            <div className="flex gap-2 justify-end">
              <Button variant="primary" onClick={() => setEditingRequirement(null)}>
                Cancel
              </Button>
              <Button
                onClick={() => {
                  handleRequirementChange(editingRequirement, 'text', editingRequirement.text);
                  handleRequirementChange(editingRequirement, 'keystone_response', editingRequirement.keystone_response);
                  setEditingRequirement(null);
                }}
              >
                Save
              </Button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}
